/**
 * storeWindow.js -- the store's open/close window rules for the editor.
 *
 * Store dates are NAIVE wall-clock datetimes ("YYYY-MM-DD HH:mm:ss"): the
 * stores table has no timezone column, and the schedule is read in the
 * distributor's timezone. Everything here therefore works on naive strings
 * and never converts through the browser's offset.
 *
 * The backend applies the same Pop-Up rule on the permanent -> Pop-Up
 * transition (Store.popup_window_on_switch in api/src/models/store.py), and
 * resolves "the store's zone" the same way (resolve_effective_timezone);
 * keep the two in step.
 */
import moment from "moment";

import { hasClosedInZone, wallClockInZone } from "@/utils/storeClock";

const NAIVE_FORMAT = "YYYY-MM-DD HH:mm:ss";

// A new Pop-Up window runs this many days from its open date.
export const POPUP_DEFAULT_DAYS = 7;

// The server's last-resort zone when neither the distributor nor the
// platform has a valid one (resolve_effective_timezone).
const LAST_RESORT_ZONE = "UTC";

function isValidZone(tz) {
  if (!tz || typeof tz !== "string") return false;
  try {
    return !!Intl.DateTimeFormat("en-US", { timeZone: tz }).resolvedOptions().timeZone;
  } catch (err) {
    return false;
  }
}

/**
 * The zone a store's schedule is read in, resolved exactly as the server
 * does (Jake, 2026-10-01): the distributor's timezone when it is set and
 * valid, else the platform default timezone, else UTC. Never the browser's
 * zone -- the editor and the server must agree on "today".
 */
export function scheduleTimeZone(distributorTz, platformTz) {
  if (isValidZone(distributorTz)) return distributorTz;
  if (isValidZone(platformTz)) return platformTz;
  return LAST_RESORT_ZONE;
}

/** Which layer `scheduleTimeZone` answered from: distributor | platform | utc. */
export function scheduleTimeZoneSource(distributorTz, platformTz) {
  if (isValidZone(distributorTz)) return "distributor";
  if (isValidZone(platformTz)) return "platform";
  return "utc";
}

/** Today's calendar date in `tz`, as "YYYY-MM-DD". */
export function todayInZone(tz, now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const map = {};
  for (const p of parts) {
    if (p.type !== "literal") map[p.type] = p.value;
  }
  return `${map.year}-${map.month}-${map.day}`;
}

function parseNaive(value) {
  if (!value) return null;
  const m = moment.utc(String(value).replace("T", " "), NAIVE_FORMAT);
  return m.isValid() ? m : null;
}

/**
 * The window a store gets when it is SWITCHED to Pop-Up (Jake, 2026-10-01):
 *
 *   open  = the existing open date if it is today-or-later in `tz`,
 *           otherwise midnight today in `tz`;
 *   close = the existing close date if it is set and strictly after the
 *           open date, otherwise open + 7 days.
 *
 * No size-based guess. A Permanent store stores no close date (see
 * newPermanentStoreWindow; switching to Permanent leaves the close date
 * alone), so a mis-click Pop-Up -> Permanent -> Pop-Up keeps the close date,
 * and a legacy far-future one is kept for the user to edit.
 *
 * Only for the switch. A store that is already Pop-Up may be open now, with a
 * past open date that must stay editable.
 */
export function popupWindowOnSwitch({ open, close, tz, now = new Date() }) {
  const midnightToday = moment.utc(todayInZone(tz, now), "YYYY-MM-DD");
  let openAt = parseNaive(open);
  if (!openAt || openAt.isBefore(midnightToday)) {
    openAt = midnightToday;
  }
  let closeAt = parseNaive(close);
  if (!closeAt || !closeAt.isAfter(openAt)) {
    closeAt = openAt.clone().add(POPUP_DEFAULT_DAYS, "days");
  }
  return { open: openAt.format(NAIVE_FORMAT), close: closeAt.format(NAIVE_FORMAT) };
}

/**
 * The open date a PERMANENT store sends (Bug Backlog #329). A Permanent store
 * has no Open date control, and a future open date keeps shoppers out, so a
 * still-future one (left over from the Pop-Up it was) becomes midnight today
 * in `tz`; a past or absent one is kept. Mirrors
 * Store.permanent_open_on_switch (api/src/models/store.py); keep the two in
 * step.
 */
export function permanentOpenDate({ open, tz, now = new Date() }) {
  const openAt = parseNaive(open);
  if (!openAt) return open;
  const nowWall = parseNaive(wallClockInZone(tz, now));
  if (!nowWall || !openAt.isAfter(nowWall)) return open;
  return moment.utc(todayInZone(tz, now), "YYYY-MM-DD").format(NAIVE_FORMAT);
}

/**
 * The window a brand-new (Permanent) store is created with: open now, and
 * NO close date (Jake, 2026-10-01). end_date NULL means "never closes" to
 * every reader, and a Permanent store ignores its close date anyway.
 */
export function newPermanentStoreWindow() {
  return {
    start_date: moment().format(NAIVE_FORMAT),
    end_date: null,
  };
}

/**
 * The close date to send when a store is re-activated or locked from the
 * store list / reports. A Pop-Up whose close date has passed gets now + 7
 * days so it does not close again at once. A PERMANENT store keeps its
 * close date untouched -- it has none (or one it kept from being a Pop-Up,
 * which it ignores) -- and so does a store whose close date is still ahead.
 * The one place for the rule the seven re-activate/lock call sites share.
 *
 * Both "has the close passed?" and the new close are read on the store's
 * `schedule_timezone` (the store list rows carry it), the same clock as the
 * list's pill and Store.has_closed. Rows without it (the report lists) read
 * the viewer's zone, as before (/gate #3975 P2-1).
 */
export function reopenedCloseDate(campaign, now = new Date()) {
  const close = campaign.end_date;
  const tz = campaign.schedule_timezone;
  if (campaign.is_permanent_store || !hasClosedInZone(close, tz, now)) {
    return close;
  }
  return moment
    .utc(wallClockInZone(tz, now), NAIVE_FORMAT)
    .add(POPUP_DEFAULT_DAYS, "days")
    .format(NAIVE_FORMAT);
}

// ---------------------------------------------------------------------------
// Start / End TIME of a Pop-Up window (card #497)
//
// The Select Range dialog's time fields are native <input type="time">, which
// only speak 24-hour "HH:mm". Everything that touches those fields goes
// through the helpers below so the editor holds ONE shape ("HH:mm") and the
// API gets the naive "YYYY-MM-DD HH:mm:ss" it takes. The time is a wall-clock
// reading in the store's schedule zone; nothing here converts zones.
// ---------------------------------------------------------------------------

const CLOCK_12H_RE = /^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp])\.?[Mm]\.?$/;
const CLOCK_24H_RE = /^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;
const DATE_TIME_RE = /^\d{4}-\d{2}-\d{2}[T ](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)Z?$/;

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Any stored time -> the native time input's 24-hour "HH:mm" ("" when there
 * is none). Reads "09:00 AM" (the shape the editor held before #497),
 * "14:30[:ss]", and a full naive/ISO datetime ("2026-10-07 09:00:00"; a
 * trailing "Z" is ignored -- store dates carry no zone of their own).
 */
export function timeInputValue(value) {
  if (value === null || value === undefined) return "";
  let text = String(value).trim();
  const dt = DATE_TIME_RE.exec(text);
  if (dt) text = dt[1];
  let hours;
  let minutes;
  const twelve = CLOCK_12H_RE.exec(text);
  if (twelve) {
    hours = Number(twelve[1]);
    minutes = Number(twelve[2]);
    if (hours < 1 || hours > 12) return "";
    const pm = twelve[3].toLowerCase() === "p";
    hours = (hours % 12) + (pm ? 12 : 0);
  } else {
    const twentyFour = CLOCK_24H_RE.exec(text);
    if (!twentyFour) return "";
    hours = Number(twentyFour[1]);
    minutes = Number(twentyFour[2]);
  }
  if (hours > 23 || minutes > 59) return "";
  return `${pad2(hours)}:${pad2(minutes)}`;
}

/**
 * A date field plus a time field -> the naive "YYYY-MM-DD HH:mm:ss" the API
 * takes, or null when the date is missing/unreadable. A missing time is
 * midnight: the UI does not mark it required, and an empty time used to
 * crash the backend parser. Only the DATE part of `date` is used (the loaded
 * store's date is a full datetime; its time lives in the time field).
 */
export function composeWindowDateTime(date, time) {
  if (!date) return null;
  const day = moment(String(date).slice(0, 10), "YYYY-MM-DD", true);
  if (!day.isValid()) return null;
  const hhmm = timeInputValue(time) || "00:00";
  return `${day.format("YYYY-MM-DD")} ${hhmm}:00`;
}

/** "14:30" -> "2:30 PM" (the reader-facing 12-hour form). "" for no time. */
export function formatClockTime(time) {
  const hhmm = timeInputValue(time);
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  return `${h % 12 || 12}:${pad2(m)} ${h < 12 ? "AM" : "PM"}`;
}

/**
 * A window moment as the owner reads it: "9:00 AM EDT", or with the date
 * "Oct 7, 9:00 AM EDT". The zone abbreviation is the one in force in `tz` on
 * that date (EDT in summer, EST in winter). An unreadable zone reads as UTC,
 * the server's last resort. "" without a readable datetime.
 */
export function windowMomentLabel(
  naive,
  tz,
  { withDate = false, yearUnlessCurrent = false, now = new Date() } = {}
) {
  const wall = parseNaive(naive);
  if (!wall) return "";
  const zone = isValidZone(tz) ? tz : LAST_RESORT_ZONE;
  // The abbreviation is looked up at the wall clock read as UTC: off by the
  // zone's offset, which only matters inside the hours around a DST change.
  const abbr =
    new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "short" })
      .formatToParts(wall.toDate())
      .find((p) => p.type === "timeZoneName")?.value || zone;
  const clock = `${formatClockTime(wall.format("HH:mm"))} ${abbr}`;
  if (!withDate) return clock;
  // ``yearUnlessCurrent``: name the year when the moment is not in `tz`'s
  // current year, so "Jan 5" cannot be read as the coming January.
  const thisYear = Number(todayInZone(zone, now).slice(0, 4));
  const day =
    yearUnlessCurrent && wall.year() !== thisYear
      ? wall.format("MMM D, YYYY")
      : wall.format("MMM D");
  return `${day}, ${clock}`;
}

/**
 * The zone a loaded store's schedule is read in, for READ-ONLY displays (the
 * editor header, Review & Launch). `campaign` carries the distributor's
 * timezone (the store list rows name it `schedule_timezone`); when neither is
 * valid the platform default answers, then UTC -- `scheduleTimeZone`.
 */
export function campaignScheduleZone(campaign, platformTz) {
  return scheduleTimeZone(
    campaign?.schedule_timezone || campaign?.distributor?.timezone,
    platformTz
  );
}

/** Does the campaign name a valid zone itself, so no platform lookup is needed? */
export function campaignHasOwnZone(campaign) {
  return isValidZone(campaign?.schedule_timezone || campaign?.distributor?.timezone);
}

/**
 * The platform default timezone (GET /platform-settings/default-timezone),
 * or null when it is unset or the call fails (the server's UTC fallback then
 * applies). Best-effort: a display must not break on it.
 */
export async function fetchPlatformTimezone(axios) {
  if (!axios || typeof axios.get !== "function") return null;
  try {
    const resp = await axios.get("/platform-settings/default-timezone");
    const value = resp?.data?.value;
    return typeof value === "string" ? value : null;
  } catch (err) {
    return null;
  }
}
